import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeftIcon,
    ArrowsRightLeftIcon,
    UserCircleIcon,
    EnvelopeIcon,
    PhoneIcon,
    WalletIcon,
    CalendarIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    LinkIcon,
    DocumentTextIcon,
    TicketIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getTransactionDetails } from "../api/walletPaymentsApi";

// ============================================================================
// Badges
// ============================================================================

const DirectionBadge = ({ direction }: { direction: string }) => {
    return direction === "CREDIT" ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1.5 text-sm font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            <ArrowUpIcon className="h-4 w-4" /> CREDIT
        </span>
    ) : (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 px-3 py-1.5 text-sm font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-400">
            <ArrowDownIcon className="h-4 w-4" /> DEBIT
        </span>
    );
};

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        COMPLETED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        REVERSED: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    };
    return (
        <span className={`inline-flex rounded-full px-3 py-1.5 text-sm font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {s}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);
};

// ============================================================================
// Info Row
// ============================================================================

const InfoRow = ({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: React.ReactNode }) => (
    <div className="flex items-start gap-3 py-2">
        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
        <div className="min-w-0">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</p>
            <p className="mt-0.5 text-sm font-medium text-slate-900 dark:text-white">{value || "—"}</p>
        </div>
    </div>
);

// ============================================================================
// Component
// ============================================================================

export const TransactionDetailsRoute = () => {
    const { transactionId } = useParams<{ transactionId: string }>();
    const navigate = useNavigate();

    const { data, isLoading, error } = useQuery({
        queryKey: ["transaction-details", transactionId],
        queryFn: () => getTransactionDetails(transactionId!),
        enabled: !!transactionId,
    });

    const tx = data?.data;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
            </div>
        );
    }
    if (error || !tx) {
        return (
            <div className="py-20 text-center">
                <p className="text-rose-500">{error ? String(error) : "Transaction not found"}</p>
                <Button variant="secondary" size="sm" className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Back */}
            <button
                onClick={() => navigate("/wallet-payments/transactions")}
                className="flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
            >
                <ArrowLeftIcon className="h-4 w-4" /> Back to Transactions
            </button>

            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                    <div className={`flex h-14 w-14 items-center justify-center rounded-2xl shadow-lg ${tx.direction === "CREDIT" ? "bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-emerald-500/25" : "bg-gradient-to-br from-rose-500 to-rose-600 shadow-rose-500/25"}`}>
                        <ArrowsRightLeftIcon className="h-7 w-7 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Transaction Details</h1>
                        <div className="mt-1 flex items-center gap-3">
                            <DirectionBadge direction={tx.direction} />
                            <StatusBadge status={tx.status} />
                        </div>
                    </div>
                </div>
                <div className={`text-3xl font-bold ${tx.direction === "CREDIT" ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                    {tx.direction === "CREDIT" ? "+" : "−"}{formatCurrency(tx.amount, tx.currency)}
                </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                {/* Transaction Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Transaction Information</h2>
                    <div className="space-y-1">
                        <InfoRow icon={ArrowsRightLeftIcon} label="Type" value={tx.type?.replace(/_/g, " ")} />
                        <InfoRow icon={DocumentTextIcon} label="Reference" value={<span className="font-mono text-xs">{tx.reference}</span>} />
                        <InfoRow icon={DocumentTextIcon} label="Description" value={tx.description} />
                        {tx.externalReference && (
                            <InfoRow icon={LinkIcon} label="External Reference" value={<span className="font-mono text-xs">{tx.externalReference}</span>} />
                        )}
                        <InfoRow icon={CalendarIcon} label="Created" value={formatDate(tx.createdAt)} />
                        <InfoRow icon={CalendarIcon} label="Processed" value={formatDate(tx.processedAt)} />
                    </div>
                </div>

                {/* User Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">User Information</h2>
                    <div className="space-y-1">
                        <InfoRow icon={UserCircleIcon} label="Name" value={`${tx.user.firstName} ${tx.user.lastName}`} />
                        <InfoRow icon={EnvelopeIcon} label="Email" value={tx.user.email} />
                        <InfoRow icon={PhoneIcon} label="Phone" value={tx.user.phoneNumber || "—"} />
                        <InfoRow icon={UserCircleIcon} label="Role" value={tx.user.role} />
                    </div>

                    {tx.wallet && (
                        <>
                            <h3 className="mb-2 mt-6 text-sm font-semibold text-slate-700 dark:text-slate-300">Wallet</h3>
                            <div className="space-y-1">
                                <InfoRow icon={WalletIcon} label="Balance" value={formatCurrency(tx.wallet.balance, tx.wallet.currency)} />
                                <InfoRow icon={WalletIcon} label="Wallet Status" value={tx.wallet.status} />
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Related Booking */}
            {tx.relatedBooking && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Related Booking</h2>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Booking ID</p>
                            <p className="mt-0.5 font-mono text-xs text-slate-900 dark:text-white">{tx.relatedBooking.bookingId}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Status</p>
                            <p className="mt-0.5 text-sm font-medium text-slate-900 dark:text-white">{tx.relatedBooking.status}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Booked At</p>
                            <p className="mt-0.5 text-sm text-slate-900 dark:text-white">{formatDate(tx.relatedBooking.bookedAt)}</p>
                        </div>
                        <div className="flex items-end">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => navigate(`/ride-bookings-trips/bookings/${tx.relatedBooking!.bookingId}`)}
                            >
                                <TicketIcon className="mr-1.5 h-4 w-4" /> View Booking
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
